import React, { useEffect, useMemo, useState } from 'react';
import { useIsTutorialMode } from '@context/PatientDataContext';
import { useAccessToken } from '@features/user';
import { usePatientFieldUpdate } from '@hooks/usePatientFieldUpdate';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import APIService from '@services/APIService';
import { MuiTelInput } from 'mui-tel-input';
import { useSnackbar } from 'notistack';

import type { Patient, ProviderBillingInfo } from '../../types/types';
import SlideSidePane from '../../Views/Shared/SlideSidePane';

import type { DeliveryChannel, PatientSendPanelProps, SendData } from './types';

export function PatientSendPanel<TContent, TSendRequest>({
  open,
  onClose,
  patientId,
  patient: providedPatient,
  variant = 'dialog',
  contentConfig,
  content,
  contentRenderer,
  requiredFields = [],
  requiredFieldsConfig = {},
  defaultChannels = ['sms'],
  allowedChannels = ['sms', 'email'],
  previewRenderer,
  prepareSendRequest,
  onSend,
  additionalFields,
  footerContent,
  validateBeforeSend,
  onSuccess,
  isLoadingPatient: externalLoadingState,
}: PatientSendPanelProps<TContent, TSendRequest>) {
  const { enqueueSnackbar } = useSnackbar();
  const { getAccessToken } = useAccessToken();
  const isTutorialMode = useIsTutorialMode();

  // Patient state
  const [patient, setPatient] = useState<Patient | null>(providedPatient || null);
  const [isLoadingPatient, setIsLoadingPatient] = useState(
    !providedPatient && !externalLoadingState,
  );
  const [userInfo, setUserInfo] = useState<ProviderBillingInfo | null>(null);

  // Form state
  const [dob, setDob] = useState(patient?.dob || '');
  const [mobilePhone, setMobilePhone] = useState(patient?.mobile_phone || patient?.phone || '');
  const [email, setEmail] = useState(patient?.email || '');
  const [customMessage, setCustomMessage] = useState('');

  // Channel selection
  const [selectedChannels, setSelectedChannels] = useState<DeliveryChannel[]>(
    defaultChannels.filter(ch => allowedChannels.includes(ch)),
  );

  // Sync patient data and form fields when providedPatient prop updates
  useEffect(() => {
    if (!providedPatient) return;
    setPatient(providedPatient);
    if (providedPatient.dob) setDob(prev => prev || providedPatient.dob || '');
    const phone = providedPatient.mobile_phone || providedPatient.phone || '';
    if (phone) setMobilePhone(prev => prev || phone);
    if (providedPatient.email) setEmail(prev => prev || providedPatient.email || '');
  }, [providedPatient]);

  // Auto-select delivery channels based on available patient contact info
  useEffect(() => {
    const patientPhone = patient?.mobile_phone || patient?.phone || mobilePhone;
    const patientEmail = patient?.email || email;
    const hasPhone = !!patientPhone;
    const hasEmail = !!patientEmail;

    if (!hasPhone && !hasEmail) return;

    setSelectedChannels(prev => {
      const validChannels = prev.filter(ch => {
        if (ch === 'sms') return hasPhone && allowedChannels.includes('sms');
        if (ch === 'email') return hasEmail && allowedChannels.includes('email');
        return true;
      });
      if (validChannels.length > 0) return validChannels;

      if (hasPhone && allowedChannels.includes('sms')) return ['sms'];
      if (hasEmail && allowedChannels.includes('email')) return ['email'];
      return prev;
    });
  }, [patient, mobilePhone, email, allowedChannels]);

  // Sending state
  const [isSending, setIsSending] = useState(false);

  // Track if we're in the send process
  const [isInSendProcess, setIsInSendProcess] = useState(false);

  // Use the patient field update hook
  const { updatePatientField, validateField, clearFieldError, isUpdating, fieldErrors } =
    usePatientFieldUpdate({
      patientId,
      onUpdate: (field, value) => {
        if (patient) {
          setPatient({ ...patient, [field]: value });
        }
      },
      showSuccessNotification: !isInSendProcess,
    });

  // Fetch patient data if not provided
  useEffect(() => {
    const fetchPatient = async () => {
      if (!providedPatient && patientId && !externalLoadingState && !isTutorialMode) {
        setIsLoadingPatient(true);
        try {
          const accessToken = await getAccessToken();
          const response = await APIService.makeAPIGetRequest({
            requestString: `/patient/get?patient_id=${patientId}&origin=PatientSendPanel`,
            accessToken,
          });

          if (response.ok && response.value) {
            const responseData = response.value as { patient: Patient };
            const patientData = responseData.patient;
            setPatient(patientData);

            // Pre-populate fields
            if (patientData.dob) setDob(patientData.dob);
            if (patientData.mobile_phone) setMobilePhone(patientData.mobile_phone);
            else if (patientData.phone) setMobilePhone(patientData.phone);
            if (patientData.email) setEmail(patientData.email);
          }
        } catch (error) {
          console.error('Error fetching patient:', error);
          enqueueSnackbar('Failed to load patient data', { variant: 'error' });
        } finally {
          setIsLoadingPatient(false);
        }
      }
    };

    fetchPatient();
  }, [patientId, providedPatient, getAccessToken, enqueueSnackbar, externalLoadingState, isTutorialMode]);

  // Fetch provider info for preview
  useEffect(() => {
    const fetchProviderInfo = async () => {
      try {
        const accessToken = await getAccessToken();
        const response = await APIService.makeAPIGetRequest({
          requestString: `/user/getProviderBillingInfo`,
          accessToken,
        });

        if (response.ok && response.value) {
          const data = (response.value as { provider_billing_info: ProviderBillingInfo })
            .provider_billing_info;
          setUserInfo(data);
        }
      } catch (error) {
        console.error('Error fetching provider information:', error);
      }
    };

    if (open) {
      fetchProviderInfo();
    }
  }, [open, getAccessToken]);

  // Handle field changes
  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDob(e.target.value);
    if (fieldErrors.dob) clearFieldError('dob');
  };

  const handlePhoneChange = (value: string) => {
    setMobilePhone(value);
    if (fieldErrors.mobile_phone) clearFieldError('mobile_phone');
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (fieldErrors.email) clearFieldError('email');
  };

  // Handle field blur (auto-save)
  const handleDobBlur = async () => {
    if (dob && dob !== patient?.dob) {
      await updatePatientField('dob', dob, patient?.dob || undefined);
    }
  };

  const handlePhoneBlur = async () => {
    if (mobilePhone && mobilePhone !== patient?.mobile_phone && mobilePhone !== patient?.phone) {
      const currentValue = patient?.mobile_phone || patient?.phone;
      await updatePatientField('mobile_phone', mobilePhone, currentValue || undefined);
    }
  };

  const handleEmailBlur = async () => {
    if (email && email !== patient?.email) {
      await updatePatientField('email', email, patient?.email || undefined);
    }
  };

  // Channel selection handlers
  const handleChannelToggle = (channel: DeliveryChannel) => {
    setSelectedChannels(prev =>
      prev.includes(channel) ? prev.filter(ch => ch !== channel) : [...prev, channel],
    );
  };

  // Determine if we can send
  const canSend = useMemo(() => {
    // Must have at least one channel selected
    if (selectedChannels.length === 0) return false;

    // Check required fields
    if (requiredFields.includes('dob') && !patient?.dob && !dob) return false;
    if (
      requiredFields.includes('mobile_phone') &&
      !patient?.mobile_phone &&
      !patient?.phone &&
      !mobilePhone
    )
      return false;
    if (requiredFields.includes('email') && !patient?.email && !email) return false;

    // Check selected channels have data
    if (
      selectedChannels.includes('sms') &&
      !patient?.mobile_phone &&
      !patient?.phone &&
      !mobilePhone
    )
      return false;
    if (selectedChannels.includes('email') && !patient?.email && !email) return false;

    // Must have content to send
    if (content.length === 0) return false;

    // No errors
    if (Object.values(fieldErrors).some(error => error !== null)) return false;

    return true;
  }, [selectedChannels, requiredFields, patient, dob, mobilePhone, email, content, fieldErrors]);

  // Handle send
  const handleSend = async () => {
    // Validate all fields
    let hasErrors = false;

    if (requiredFields.includes('dob') && !patient?.dob) {
      if (!validateField('dob', dob)) hasErrors = true;
    }

    if (selectedChannels.includes('sms') && !patient?.mobile_phone && !patient?.phone) {
      if (!validateField('mobile_phone', mobilePhone)) hasErrors = true;
    }

    if (selectedChannels.includes('email') && !patient?.email) {
      if (!validateField('email', email)) hasErrors = true;
    }

    if (hasErrors) return;

    // Custom validation if provided
    if (validateBeforeSend) {
      const isValid = await validateBeforeSend();
      if (!isValid) return;
    }

    setIsSending(true);
    setIsInSendProcess(true);

    try {
      // Update patient fields if needed
      const updates: Array<Promise<boolean>> = [];

      if (requiredFields.includes('dob') && !patient?.dob && dob) {
        updates.push(updatePatientField('dob', dob));
      }

      if (
        selectedChannels.includes('sms') &&
        !patient?.mobile_phone &&
        !patient?.phone &&
        mobilePhone
      ) {
        updates.push(updatePatientField('mobile_phone', mobilePhone));
      }

      if (selectedChannels.includes('email') && !patient?.email && email) {
        updates.push(updatePatientField('email', email));
      }

      if (updates.length > 0) {
        const updateResults = await Promise.all(updates);
        if (updateResults.some(result => !result)) {
          setIsSending(false);
          setIsInSendProcess(false);
          return;
        }
      }

      // Prepare the send data
      const sendData: SendData = {
        channels: selectedChannels,
        patient: {
          ...(patient || ({} as Patient)),
          dob: dob || patient?.dob || '',
          mobile_phone: mobilePhone || patient?.mobile_phone || patient?.phone || '',
          email: email || patient?.email || '',
        },
        customMessage: customMessage || undefined,
      };

      // Prepare the request
      const request = prepareSendRequest(sendData, content);

      // Send the request
      const result = await onSend(request);

      if (result.success) {
        const channelText =
          selectedChannels.length === 2
            ? 'SMS and Email'
            : selectedChannels[0] === 'sms'
              ? 'SMS'
              : 'Email';
        enqueueSnackbar(`${contentConfig.itemLabel} sent successfully via ${channelText}`, {
          variant: 'success',
        });
        onSuccess?.(result);
        onClose();
      } else {
        throw new Error(
          result.message || `Failed to send ${contentConfig.itemLabel.toLowerCase()}`,
        );
      }
    } catch (error) {
      console.error(`Error sending ${contentConfig.itemLabel}:`, error);
      enqueueSnackbar(
        error instanceof Error
          ? error.message
          : `Failed to send ${contentConfig.itemLabel.toLowerCase()}`,
        { variant: 'error' },
      );
    } finally {
      setIsSending(false);
      setIsInSendProcess(false);
    }
  };

  // Determine delivery method for preview
  const deliveryMethod = useMemo(() => {
    if (selectedChannels.includes('sms') && selectedChannels.includes('email')) return 'both';
    if (selectedChannels.includes('sms')) return 'sms';
    if (selectedChannels.includes('email')) return 'email';
    return 'sms';
  }, [selectedChannels]);

  // Loading state
  if (isLoadingPatient || externalLoadingState) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Content wrapper based on variant
  const contentBody = (
    <Stack spacing={3}>
      {/* Content Display */}
      <Box>
        <Typography variant="subtitle2" gutterBottom sx={{ color: '#6B6B6B', fontWeight: 600 }}>
          {contentConfig.pluralLabel} Being Sent
        </Typography>
        <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
          {content.map((item, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              {contentRenderer(item, index)}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Additional Fields */}
      {additionalFields && <Box>{additionalFields}</Box>}

      {/* Required Fields */}
      {requiredFields.includes('dob') && !patient?.dob && (
        <TextField
          label="Date of Birth"
          type="date"
          value={dob}
          onChange={handleDobChange}
          onBlur={handleDobBlur}
          error={!!fieldErrors.dob}
          helperText={
            fieldErrors.dob ||
            requiredFieldsConfig?.dob?.helpText ||
            'Patient date of birth is required'
          }
          disabled={isUpdating}
          required
          size="small"
          fullWidth
         slotProps={{ inputLabel: { shrink: true } }}/>
      )}

      {/* Delivery Channels */}
      <Box>
        <Typography variant="subtitle2" gutterBottom sx={{ color: '#6B6B6B', fontWeight: 600 }}>
          Select Delivery Method(s)
        </Typography>
        <FormGroup>
          {allowedChannels.includes('sms') && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedChannels.includes('sms')}
                    onChange={() => handleChannelToggle('sms')}
                  />
                }
                label="SMS"
              />
              {selectedChannels.includes('sms') && (
                <Box sx={{ ml: 4, mb: 2 }}>
                  <MuiTelInput
                    value={mobilePhone}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    defaultCountry="US"
                    label="Phone Number"
                    size="small"
                    fullWidth
                    error={!!fieldErrors.mobile_phone}
                    helperText={fieldErrors.mobile_phone}
                    disabled={isUpdating}
                  />
                </Box>
              )}
            </Box>
          )}

          {allowedChannels.includes('email') && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedChannels.includes('email')}
                    onChange={() => handleChannelToggle('email')}
                  />
                }
                label="Email"
              />
              {selectedChannels.includes('email') && (
                <Box sx={{ ml: 4, mb: 2 }}>
                  <TextField
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    label="Email Address"
                    type="email"
                    size="small"
                    fullWidth
                    error={!!fieldErrors.email}
                    helperText={fieldErrors.email}
                    disabled={isUpdating}
                  />
                </Box>
              )}
            </Box>
          )}
        </FormGroup>
      </Box>

      {/* Custom Message */}
      <TextField
        label="Custom Message (optional)"
        value={customMessage}
        onChange={e => setCustomMessage(e.target.value)}
        multiline
        rows={3}
        placeholder="Add a personal message to include"
        fullWidth
      />

      {/* Preview */}
      {previewRenderer && selectedChannels.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom sx={{ color: '#6B6B6B', fontWeight: 600 }}>
            Preview
          </Typography>
          {previewRenderer({
            deliveryMethod,
            patientName: patient?.first_name,
            providerName: userInfo?.communications_name,
            content,
            customMessage,
          })}
        </Box>
      )}
    </Stack>
  );

  // Inline variant - renders content directly without a wrapper
  if (variant === 'inline') {
    if (!open) return null;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {contentConfig.title}
        </Typography>
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>{contentBody}</Box>
        <Box sx={{ pt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button onClick={onClose} color="inherit">
            Back
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            disabled={!canSend || isSending || isUpdating}
            sx={{
              bgcolor: '#4D32D7',
              '&:hover': { bgcolor: '#36229e' },
            }}
          >
            {isSending || isUpdating ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                {isUpdating ? 'Updating...' : 'Sending...'}
              </>
            ) : (
              `Send ${contentConfig.itemLabel}`
            )}
          </Button>
        </Box>
      </Box>
    );
  }

  // Dialog variant
  if (variant === 'dialog') {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
       maxWidth={"sm"} slotProps={{ paper: { sx: { borderRadius: 2 } } }}>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6">{contentConfig.title}</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>{contentBody}</DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            disabled={!canSend || isSending || isUpdating}
            sx={{
              bgcolor: '#4D32D7',
              '&:hover': { bgcolor: '#36229e' },
            }}
          >
            {isSending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              `Send ${contentConfig.itemLabel}`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Slide panel variant
  return (
    <SlideSidePane open={open} onClose={onClose} title={contentConfig.title}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>{contentBody}</Box>
        <Box sx={{ pt: 2, pb: 1, mx: 1, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSend}
            disabled={!canSend || isSending || isUpdating}
            size="large"
            sx={{ height: 48, textTransform: 'none', borderRadius: 2 }}
          >
            {isSending || isUpdating ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {isUpdating ? 'Updating Patient Info...' : 'Sending...'}
              </>
            ) : (
              `Send ${contentConfig.itemLabel}`
            )}
          </Button>
          {footerContent && <Box sx={{ mt: 2 }}>{footerContent}</Box>}
        </Box>
      </Box>
    </SlideSidePane>
  );
}
